import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, LogOut, Home, ClipboardList, Car, TriangleAlert, LayoutList, CheckSquare, Upload, Users, Bell, CalendarDays, ShieldCheck, MailCheck, Network, SlidersHorizontal, Inbox, History, Building2, FileClock, FileSpreadsheet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup, RelayMark } from "@/components/brand";
import { StaffInstallButton } from "@/components/pwa";
import { AlertsBell } from "@/components/alerts";
import { SpeakToHuman } from "@/components/SpeakToHuman";
import { ROLE_LABEL } from "@/lib/vda";

const DRIVER_NAV = [
  { to: "/driver", label: "Home", icon: Home },
  { to: "/driver/history", label: "Requests", icon: ClipboardList },
  { to: "/driver/vehicle", label: "Vehicle", icon: Car },
  { to: "/driver/breakdown", label: "Urgent", icon: TriangleAlert },
];

// Mobile-first driver shell — simpler and more human than the internal console
export function DriverShell({ title, back, children }) {
  const { signOut } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 bg-card border-b border-border">
        <div className="mx-auto max-w-md px-4 h-14 flex items-center justify-between">
          {back ? (
            <button data-testid="back-btn" onClick={() => nav(back)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <BrandLockup compact />
          )}
          <div className="flex items-center gap-2">
            <SpeakToHuman />
            <button data-testid="signout-btn" aria-label="Sign out" title="Sign out" onClick={() => signOut().then(() => nav("/verify"))} className="p-1 text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-md px-4 pb-28 pt-5 vda-rise">
        {title && <h1 className="text-[26px] font-semibold tracking-tight text-foreground mb-4">{title}</h1>}
        {children}
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-card/95 backdrop-blur border-t border-border" data-testid="driver-bottom-nav">
        <div className="mx-auto max-w-md grid grid-cols-4">
          {DRIVER_NAV.map((i) => {
            const active = loc.pathname === i.to;
            const Icon = i.icon;
            return (
              <Link key={i.to} to={i.to} data-testid={`drivernav-${i.label.toLowerCase()}`} aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 min-h-[58px] text-[11px] transition-colors ${active ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
                {i.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

const STAFF_NAV = [
  { to: "/staff", label: "Queues", icon: LayoutList },
  { to: "/staff/bulk", label: "Bulk update", icon: CheckSquare },
  { to: "/staff/confirmations", label: "Confirmations", icon: MailCheck },
  { to: "/staff/confirmations/audit", label: "Conf. audit", icon: History, oversight: true },
  { to: "/staff/ingestion", label: "Ingestion", icon: Inbox },
  { to: "/staff/other-pods", label: "Other Pods", icon: Network },
  { to: "/staff/am-view", label: "Customer view", icon: Building2, oversight: true },
  { to: "/staff/compliance", label: "Compliance", icon: FileClock, oversight: true },
  { to: "/staff/operational-imports", label: "Op. imports", icon: FileSpreadsheet, oversight: true },
  { to: "/staff/imports", label: "Imports", icon: Upload },
  { to: "/staff/allocations", label: "Allocations", icon: Car },
  { to: "/staff/oversight", label: "Oversight", icon: Users, oversight: true },
  { to: "/staff/notifications", label: "Notifications", icon: Bell },
  { to: "/staff/customer-config", label: "Customer setup", icon: SlidersHorizontal, adminOnly: true },
  { to: "/staff/calendars", label: "Calendars", icon: CalendarDays, adminOnly: true },
  { to: "/staff/security", label: "Security", icon: ShieldCheck, adminOnly: true },
];

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "U";
}

export function StaffShell({ children }) {
  const { signOut, profile, role } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const showOversight = ["account_manager", "pod_lead", "administrator"].includes(role);
  const isAdmin = role === "administrator";
  const items = STAFF_NAV.filter(
    (i) => (i.oversight ? showOversight : true) && (i.adminOnly ? isAdmin : true)
  );
  const RailIcon = ({ i }) => {
    const active = loc.pathname === i.to;
    const Icon = i.icon;
    return (
      <Link key={i.to} to={i.to} data-testid={`nav-${i.label.toLowerCase().replace(/ /g, "-")}`}
        aria-current={active ? "page" : undefined} title={i.label} aria-label={i.label}
        className={`grid place-items-center h-[42px] w-[42px] rounded-[6px] transition-colors ${active ? "bg-[hsl(var(--vda-green-bg))] text-primary ring-1 ring-inset ring-primary/25" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden="true" />
      </Link>
    );
  };
  return (
    <div className="min-h-screen bg-background flex">
      {/* Left icon rail (desktop) */}
      <aside className="hidden md:flex sticky top-0 h-screen w-16 flex-none flex-col items-center border-r border-border bg-card z-30" data-testid="staff-rail">
        <Link to="/staff" className="h-14 w-full grid place-items-center border-b border-border" title="relay. by AVENIR">
          <RelayMark size="text-lg" className="text-primary" />
        </Link>
        <nav className="flex flex-col items-center gap-1 w-full py-3">
          {items.map((i) => <RailIcon key={i.to} i={i} />)}
        </nav>
        <div className="flex-1" />
        <div className="mb-3 grid h-[34px] w-[34px] place-items-center rounded-full border border-border bg-card text-[10px] font-bold" title={profile?.full_name}>
          {initials(profile?.full_name)}
        </div>
      </aside>

      <div className="flex-1 min-w-0 pb-16 md:pb-0">
        {/* Slim top context bar */}
        <header className="sticky top-0 z-20 bg-card border-b border-border">
          <div className="px-5 h-14 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 md:hidden">
              <BrandLockup compact />
            </div>
            <div className="hidden md:block text-xs text-muted-foreground font-mono uppercase tracking-wider">Operations workspace</div>
            <div className="flex items-center gap-3">
              <div className="text-right leading-tight">
                <div className="text-sm font-medium">{profile?.full_name}</div>
                <div className="text-xs text-muted-foreground">{ROLE_LABEL[role]}</div>
              </div>
              <StaffInstallButton />
              <AlertsBell />
              <button data-testid="staff-signout" onClick={() => signOut().then(() => nav("/login"))} className="p-2 hover:bg-secondary rounded-[6px] text-muted-foreground hover:text-foreground" title="Sign out">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1600px] px-5 lg:px-8 py-6 vda-rise">{children}</main>
      </div>

      {/* Mobile bottom rail */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t border-border overflow-x-auto" data-testid="staff-mobile-nav">
        <div className="flex min-w-max">
          {items.map((i) => {
            const active = loc.pathname === i.to;
            const Icon = i.icon;
            return (
              <Link key={i.to} to={i.to} aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 px-4 py-2 min-h-[56px] text-[10px] whitespace-nowrap ${active ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
                {i.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// Fleet-manager workspace shell — same app-shell pattern as the internal StaffShell
// (persistent left icon rail on desktop, slim top context bar, scrollable mobile bottom nav).
// `nav` is config-driven and supplied by FleetLayout; falls back to the 3 core destinations.
const FLEET_NAV = [
  { key: "dashboard", to: "/fleet", label: "Dashboard", icon: Home, match: (p) => p === "/fleet" },
  { key: "vehicles", to: "/fleet/vehicles", label: "Vehicles", icon: Car, match: (p) => p.startsWith("/fleet/vehicle") },
  { key: "requests", to: "/fleet/requests", label: "Requests", icon: ClipboardList, match: (p) => p.startsWith("/fleet/requests") },
];

export function FleetShell({ org, headerRight, nav, children }) {
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const items = nav || FLEET_NAV;
  const isActive = (i) => (i.match ? i.match(loc.pathname) : loc.pathname === i.to);
  return (
    <div className="min-h-screen bg-background flex">
      {/* Left icon rail (desktop) */}
      <aside className="hidden md:flex sticky top-0 h-screen w-16 flex-none flex-col items-center border-r border-border bg-card z-30" data-testid="fleet-rail">
        <Link to="/fleet" className="h-14 w-full grid place-items-center border-b border-border" title="relay. by AVENIR">
          <RelayMark size="text-lg" className="text-primary" />
        </Link>
        <nav className="flex flex-col items-center gap-1 w-full py-3 overflow-y-auto">
          {items.map((i) => {
            const active = isActive(i);
            const Icon = i.icon;
            const danger = i.tone === "danger";
            const cls = danger
              ? (active ? "bg-[hsl(var(--vda-red))]/12 text-[hsl(var(--vda-red))] ring-1 ring-inset ring-[hsl(var(--vda-red))]/30" : "text-[hsl(var(--vda-red))]/85 hover:bg-[hsl(var(--vda-red))]/10 hover:text-[hsl(var(--vda-red))]")
              : (active ? "bg-[hsl(var(--vda-green-bg))] text-primary ring-1 ring-inset ring-primary/25" : "text-muted-foreground hover:bg-secondary hover:text-foreground");
            return (
              <Link key={i.to} to={i.to} data-testid={`fleetnav-${i.key}`}
                aria-current={active ? "page" : undefined} title={i.label} aria-label={i.label}
                className={`grid place-items-center h-[42px] w-[42px] rounded-[6px] transition-colors ${cls}`}>
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden="true" />
              </Link>
            );
          })}
        </nav>
        <div className="flex-1" />
        <div className="mb-3 grid h-[34px] w-[34px] place-items-center rounded-full border border-border bg-card text-[10px] font-bold" title={profile?.full_name}>
          {initials(profile?.full_name)}
        </div>
      </aside>

      <div className="flex-1 min-w-0 pb-16 md:pb-0">
        {/* Slim top context bar */}
        <header className="sticky top-0 z-20 bg-card border-b border-border">
          <div className="px-5 h-14 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 md:hidden">
              <BrandLockup compact />
            </div>
            <div className="hidden md:block text-xs text-muted-foreground font-mono uppercase tracking-wider">Fleet workspace</div>
            <div className="flex items-center gap-3">
              <div className="text-right leading-tight hidden sm:block">
                <div className="text-sm font-medium">{org}</div>
                <div className="text-xs text-muted-foreground">Fleet manager</div>
              </div>
              {headerRight}
              <AlertsBell />
              <button data-testid="external-signout" onClick={() => signOut().then(() => navigate("/login"))} className="p-2 hover:bg-secondary rounded-[6px] text-muted-foreground hover:text-foreground" title="Sign out">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1400px] px-5 lg:px-8 py-6 vda-rise">{children}</main>
      </div>

      {/* Mobile bottom rail — scrollable to accommodate config-driven destinations */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t border-border overflow-x-auto" data-testid="fleet-mobile-nav">
        <div className="flex min-w-max">
          {items.map((i) => {
            const active = isActive(i);
            const Icon = i.icon;
            const danger = i.tone === "danger";
            const cls = danger
              ? (active ? "text-[hsl(var(--vda-red))] font-semibold" : "text-[hsl(var(--vda-red))]/80")
              : (active ? "text-primary font-semibold" : "text-muted-foreground");
            return (
              <Link key={i.to} to={i.to} data-testid={`fleetnav-m-${i.key}`} aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 px-4 py-2 min-h-[56px] text-[10px] whitespace-nowrap transition-colors ${cls}`}>
                <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
                {i.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
